import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CoopValidators } from '@core/helpers/validators.helper';
import { NaturaMutualisticaFormValidators } from './validators/natura-mutualistica.form-validators';
import { NatMutualisticaWarningsService } from './warnings/nat-mutualistica.warnings';
import { MyVerbale } from '../../models/interfaces/revisione.interface';
import { setInitialTipoSezEnte } from '../../helpers/form-store.helpers';
import { NaturaMutualisticaFormListeners } from './listeners/natura-mutualistica.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import { formPaths } from '@shared/constants/form-paths.const';

@Injectable()
export class NaturaMutualisticaFormService extends BaseRevisioneFormService<NatMutualisticaWarningsService> {
    constructor(protected warningsService: NatMutualisticaWarningsService) {
        super(warningsService);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Stato dati
            'my_DBPresente',
            'my_numBilanciNelDBCentrale',

            // Flag prevalenza
            'my_InseritoB9VersoSoci',
            'my_InseritoA1VersoSoci',
            'my_InseritoB7VersoSoci',
            'my_InseritoB6QtaVersoSoci',
            'my_InseritoB6VersoSoci',

            // Bilancio
            `${formPaths.bilancio}.my_NumBilanciInExcel`,
            `${formPaths.bilancio}.my_NoteAlBilancio`,
            `${formPaths.bilancio}.my_BilancioAllegato`,

            // Scheda coop sociali
            `${formPaths.schedaCoopSociali}.my_ScSocObbl`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_DataChiusura2`,
            `${formPaths.datiBilancio}.my_DataChiusura1`,
            `${formPaths.datiBilancio}.my_TipoBilancioAbbr1`,
            `${formPaths.datiBilancio}.my_DataApprovazione1`,
            `${formPaths.datiBilancio}.my_TipoSchemaBil1`,

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,
            `${formPaths.datiIdentificativi}.my_TipoSezEnte`,
            `${formPaths.datiIdentificativi}.my_PosizAlboEnti`,
            `${formPaths.datiIdentificativi}.my_RegimeDerogatorioProvalenza`,
            `${formPaths.datiIdentificativi}.my_FakeRegimeDerogatorioProvalenza`,
            `${formPaths.datiIdentificativi}.my_DescRegimeDerogatorioProvalenza`,
            `${formPaths.datiIdentificativi}.my_FakeDescRegimeDerogatorioProvalenza`,
            `${formPaths.datiIdentificativi}.my_CkRientranteInRegimeDerogatorio`,
            `${formPaths.datiIdentificativi}.my_CkAttivEffettSvolta`,
            `${formPaths.datiIdentificativi}.my_AttivEffettSvolta`,
            `${formPaths.datiIdentificativi}.my_OggSocStatuto`,
            `${formPaths.datiIdentificativi}.my_AttivSvoltaConcreta`,
            `${formPaths.datiIdentificativi}.my_CkMutualPrev`,
        ]);
    }

    override initListener(): void {
        new NaturaMutualisticaFormListeners(this._form, this.store).setListeners();
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_DBPresente: '',
                my_numBilanciNelDBCentrale: '',
                my_InseritoB9VersoSoci: '',
                my_InseritoA1VersoSoci: '',
                my_InseritoB7VersoSoci: '',
                my_InseritoB6QtaVersoSoci: '',
                my_InseritoB6VersoSoci: '',
                my_SezioneI__Rilevazione: this.fb.group({
                    my_Bilancio: this.fb.group({
                        my_NumBilanciInExcel: '',
                        my_NoteAlBilancio: '',
                        my_BilancioAllegato: '',
                    }),

                    my_SchedaCoopSociali: this.fb.group({
                        my_ScSocObbl: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_BilAnno2: '',
                        my_BilAnno1: '',
                        my_DataChiusura2: '',
                        my_DataChiusura1: '',
                        my_TipoBilancioAbbr1: '',
                        my_DataApprovazione1: '',
                        my_TipoSchemaBil1: '',
                    }),

                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                        my_TipoSezEnte: '',
                        my_PosizAlboEnti: '',
                        my_RegimeDerogatorioProvalenza: '',
                        my_FakeRegimeDerogatorioProvalenza: '',
                        my_DescRegimeDerogatorioProvalenza: '',
                        my_FakeDescRegimeDerogatorioProvalenza: '',
                        my_CkRientranteInRegimeDerogatorio: '',
                        my_CkAttivEffettSvolta: ['', CoopValidators.required],
                        my_AttivEffettSvolta: '',
                        my_OggSocStatuto: ['', CoopValidators.required],
                        my_AttivSvoltaConcreta: ['', CoopValidators.required],
                        my_CkMutualPrev: '',
                    }),

                    my_NaturaMutualistica: this.fb.group({
                        my_CkScopoMutual: ['', CoopValidators.required],
                        my_DescScopoMutualStat: '',
                        my_DescScopoMutual: '',
                        my_CkObblDestinazUtil: ['', CoopValidators.required],
                        my_CkReqMutualPrev: ['', CoopValidators.required],
                        my_CkMutuaDivid: '',
                        my_CkMutuaRiserve: '',
                        my_NoteProcedureCambioSezione: '',
                        my_CkNuovoSocioCatSpec: ['', CoopValidators.required],
                        my_CkAttivConTerzi: ['', CoopValidators.required],
                        my_CkDefCriteriRistorni: ['', CoopValidators.required],
                        my_CkAssSeparate: ['', CoopValidators.required],
                        my_CkAssSpeciali: ['', CoopValidators.required],
                        my_CkStatutoConforme: ['', CoopValidators.required],
                        my_CkOffreBeniServ: '',
                        my_CkOffreOccLavSoci: ['', CoopValidators.required],
                        my_CkRemunConferProdSoci: ['', CoopValidators.required],
                        my_CkRemunPrestConfServSoci: ['', CoopValidators.required],
                        my_StatConfNorm: '',
                        my_OssRispettoPrevis: ['', CoopValidators.required],
                        my_CkRegolamInterni: ['', CoopValidators.required],
                        my_CkRegolamInterniConformi: '',
                        my_AspettiOggRegolam: '',
                        my_CkParitaSociCoop: ['', CoopValidators.required],
                        my_DescParitaSociCoop: ['', CoopValidators.required],
                        my_CkDistScambioMutual: ['', CoopValidators.required],
                        my_DescScambioMutual: ['', CoopValidators.required],
                        my_CkCondizPrevNotaIntegr: ['', CoopValidators.required],
                        my_CkObbDevolUtili: ['', CoopValidators.required],
                        my_DescCondizPrevNotaIntegr: '',
                        my_CkAttPrevalSoci: ['', CoopValidators.required],
                        my_CkRientraRegDerogatori: '',
                        my_CkEsisteRipDividSociMutNP: '',
                        my_CkRispettoGestStrumFinanziari: '',
                        my_CkRipDividSociMutNP: '',
                        my_CkCostituiteRiserveDivisibili: '',
                        my_CkRiserveIndivis: '',
                        my_CkGestSeparata: '',
                        my_CkRiserveDivisibiliRipartite: '',
                        my_CkRiserveDivisibiliRispetPrevision: '',
                        my_CkRispCritPrev: '',
                        my_CkSonoStatiRipDividSociMutP: '',
                        my_CkRipDividSociMutP: '',
                        my_CkEmessoStrumFinanziari: '',
                        my_CkRemuneratiStrumFinanziari: '',
                        my_CkConsidMutualPrev: '',
                        my_CkCambiatoSezione: ['', CoopValidators.required],
                        my_CkAdempiutoProcedureCambioSez: '',
                        my_NatMutNote: ['', CoopValidators.required],
                        my_SchedaPrevalenza: this.fb.group({
                            my_lblScheda: '',
                            my_TotPrevalSoci: '',
                            my_NumIscrAlboCoopMutP: '',
                            my_TotPrevalTot: '',
                            my_CkOffreBeniServCoopAttPrimoAnno: '',
                            my_CkOffreBeniServCoopAtt: '',
                            my_CkOffreOccLavSociCoopAttPrimoAnno: '',
                            my_CkOffreOccLavSociCoopAtt: '',
                            my_CkRemunConferProdSociCoopAttPrimoAnno: '',
                            my_CkRemunConferProdSociCoopAtt: '',
                            my_CkRemunPrestConfServSociCoopAttPrimoAnno: '',
                            my_CkRemunPrestConfServSociCoopAtt: '',
                            my_CoopInattivaCommPrimoAnno: '',
                            my_CoopInattivaComm: '',
                            my_A1versoSociPrimoAnno: '',
                            my_CkIscrizAlboCoopMutP: '',
                            my_A1versoSoci: '',
                            my_TotaleA1: '',
                            my_TotaleA1PrimoAnno: '',
                            my_TotaleA1NonModifPrimoAnno: '',
                            my_TotaleA1NonModif: '',
                            my_OffreBeniServCommPrimoAnno: '',
                            my_OffreBeniServComm: '',
                            my_validaScheda: '',
                            my_validaScheda1: '',
                            my_B9versoSociPrimoAnno: '',
                            my_B9versoSoci: '',
                            my_TotaleB9PrimoAnno: '',
                            my_TotaleB9: '',
                            my_TotaleB9NonModifPrimoAnno: '',
                            my_TotaleB9NonModif: '',
                            my_OffreOccLavSociCommPrimoAnno: '',
                            my_OffreOccLavSociComm: '',
                            my_CkValoreQuantitaPrimoAnno: '',
                            my_CkValoreQuantita: ['', CoopValidators.required],
                            my_B6confSociPrimoAnno: '',
                            my_B6confSoci: '',
                            my_QtaConfSociPrimoAnno: '',
                            my_QtaConfSoci: '',
                            my_TotQtaConfSociPrimoAnno: '',
                            my_TotQtaConfSoci: '',
                            my_TotaleB6PrimoAnno: '',
                            my_TotaleB6: '',
                            my_RemunConferProdSociCommPrimoAnno: '',
                            my_RemunConferProdSociComm: '',
                            my_B7ricevSociPrimoAnno: '',
                            my_B7ricevSoci: '',
                            my_TotaleB7PrimoAnno: '',
                            my_TotaleB7: '',
                            my_TotaleB7NonModifPrimoAnno: '',
                            my_TotaleB7NonModif: '',
                            my_RemunPrestConfServSociCommPrimoAnno: '',
                            my_RemunPrestConfServSociComm: '',
                            my_TotaleB6NonModifPrimoAnno: '',
                            my_TotaleB6NonModif: '',
                            TotaleB7NonModifPrimoAnno: '',
                            TotaleB7NonModif: '',
                            TotaleB9NonModifPrimoAnno: '',
                            TotaleB9NonModif: '',
                            TotaleA1NonModif: '',
                            TotaleA1NonModifPrimoAnno: '',
                        }),
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: NaturaMutualisticaFormValidators,
            }
        );
    }

    protected beforePopulateForm(myVerbale: MyVerbale) {
        setInitialTipoSezEnte(myVerbale, this.store);
    }
}
